import { isPreviewableInSpace } from 'shared';

import { BackendRoute } from '../config/index';
import { HttpException } from '../exceptions/index';
import { Locale } from '../lang/index';
import { accessTokenStorage } from '../storage/auth/index';
import { rememberUploadedFile } from '../utils/uploaded-file-cache.util';
import { filenameFromContentDisposition } from './content-disposition.util';
import { HttpService } from './http-service.util';
import { QueryParams, QueryParamsBuilder } from './query-param-builder.util';
import { buildRequestTimeoutSignal } from './request-timeout-signal.util';

const REFRESH_REQUEST_TIMEOUT_MS = 20_000;
/** A preview tab reads its blob after `window.open` returns, so the URL is released a minute later. */
const PREVIEW_URL_REVOKE_DELAY_MS = 60_000;
/** Dev-only: minimum loading time for `/events` API calls (remove after loading UI testing). */
// const EVENTS_REQUEST_MIN_DELAY_MS = process.env.NODE_ENV === 'development' ? 4_000 : 0;

/** Must exceed refresh fetch timeout so hung refresh cannot block the app indefinitely. */
const REFRESH_LOCK_WATCHDOG_MS = REFRESH_REQUEST_TIMEOUT_MS + 10_000;

export class ApiService extends HttpService {
  private queryParamBuilder = new QueryParamsBuilder();
  constructor(public readonly backendUrl = process.env.REACT_APP_BACKEND_URL) {
    super();
  }
  private refreshLocked = false;
  private refreshLockCallbacks: ((error?: Error) => void)[] = [];
  private refreshLockWatchdogId: ReturnType<typeof setTimeout> | undefined;
  private pendingSessionCleanup: null | Promise<void> = null;

  public buildRoutePath<T extends number | string>(routePath: T[] = []) {
    return routePath.length > 0 ? '/' + routePath.join('/') : '';
  }

  public notifyRefreshLock(error?: Error) {
    if (this.refreshLockWatchdogId !== undefined) {
      clearTimeout(this.refreshLockWatchdogId);
      this.refreshLockWatchdogId = undefined;
    }
    const callbacks = [...this.refreshLockCallbacks];
    this.refreshLockCallbacks = [];
    callbacks.forEach((callback) => callback(error));
  }

  private releaseStuckRefreshLock(): void {
    if (!this.refreshLocked) {
      return;
    }
    this.refreshLocked = false;
    void this.clearStaleAuthSession();
    this.notifyRefreshLock(new Error('Token refresh timed out'));
  }

  /** Drops access token and refresh cookie without entering the refresh-retry path. */
  public clearStaleAuthSession(): Promise<void> {
    if (!this.pendingSessionCleanup) {
      this.pendingSessionCleanup = this.runStaleAuthSessionCleanup().finally(() => {
        this.pendingSessionCleanup = null;
      });
    }

    return this.pendingSessionCleanup;
  }

  private async runStaleAuthSessionCleanup(): Promise<void> {
    accessTokenStorage.getState().delete();
    try {
      await fetch(this.getJoinedPath(BackendRoute.AUTH_LOGOUT), {
        method: 'DELETE',
        credentials: 'include',
        signal: buildRequestTimeoutSignal(),
      });
    } catch {
      // Expired or missing refresh cookie is expected during cleanup.
    }
  }

  private isAuthRefreshRoute(route: string): boolean {
    return route.includes(BackendRoute.AUTH_REFRESH);
  }

  private isEventsRoute(route: string): boolean {
    return !route.includes('/companies') || !route.includes('/auth');
  }

  public async delete<T, B>(
    { endpoint, routePath = [] }: { endpoint: BackendRoute; routePath?: (number | string)[] },
    {
      body,
      headers,
      ...params
    }: {
      body?: B;
      headers?: Record<string, string>;
      locale?: Locale;
      queryParams?: QueryParams;
    } = {},
  ) {
    return this.requestWithAuth<T>(endpoint + this.buildRoutePath(routePath), {
      init: {
        method: 'DELETE',
        body: JSON.stringify(body),
        headers,
      },
      ...params,
    });
  }

  private async fetchFileBlob<T>(
    {
      endpoint,
      routePath = [],
    }: {
      endpoint: BackendRoute;
      routePath?: (number | string)[];
    },
    {
      body,
      locale = Locale.EN,
      queryParams,
      timeoutMs,
    }: { body?: T; locale?: Locale; queryParams?: QueryParams; timeoutMs?: number } = {},
  ): Promise<{ blob: Blob; filename: string }> {
    const url = super.buildQueryParam(
      this.getJoinedPath(endpoint + this.buildRoutePath(routePath)),
      queryParams,
    );

    const request = () =>
      fetch(url, {
        method: body ? 'POST' : 'GET',
        body: JSON.stringify(body),
        credentials: 'include',
        signal: buildRequestTimeoutSignal(null, timeoutMs),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessTokenStorage.getState().get() || ''}`,
          locale,
        },
      });

    const makeRequest = async (): Promise<Response> => {
      const res = await request();

      if (res.status === 401) {
        // Attempt to refresh token
        try {
          await this.refreshTokens();
        } catch (_ignore) {
          // If refresh fails, throw the original 401 error
          throw new HttpException(res, await res.json().catch(() => ({ message: 'Unauthorized' })));
        }

        // Retry with refreshed token (outside try-catch to preserve actual errors)
        const retryRes = await request();

        if (!retryRes.ok) {
          throw new HttpException(
            retryRes,
            await retryRes.json().catch(() => ({ message: 'Request failed' })),
          );
        }

        return retryRes;
      }

      if (!res.ok) {
        throw new HttpException(res, await res.json().catch(() => ({ message: 'Request failed' })));
      }

      return res;
    };

    const res = await makeRequest();

    return {
      blob: await res.blob(),
      filename: filenameFromContentDisposition(res?.headers?.get('Content-Disposition')),
    };
  }

  public async previewFile<T>(
    location: { endpoint: BackendRoute; routePath?: (number | string)[] },
    options: { body?: T; locale?: Locale; queryParams?: QueryParams } = {},
  ) {
    const { blob, filename } = await this.fetchFileBlob(location, options);

    if (!isPreviewableInSpace(blob.type)) return this.downloadFromBlob({ blob, filename });

    const fileUrl = window.URL.createObjectURL(blob);
    const opened = window.open(fileUrl);

    // The tab keeps reading the blob after the call returns, so the URL outlives it.
    setTimeout(() => URL.revokeObjectURL(fileUrl), PREVIEW_URL_REVOKE_DELAY_MS);

    // A popup the browser blocked after the await is a failure the caller reports, not a silent no-op.
    if (!opened) throw new Error('Preview window was blocked');
  }

  private downloadFromBlob({ blob, filename }: { blob: Blob; filename: string }) {
    const fileUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = fileUrl;
    a.download = filename;

    document.body.appendChild(a);

    a.click();
    a.remove();

    URL.revokeObjectURL(fileUrl);
  }

  public async downloadFile<T>(
    location: { endpoint: BackendRoute; routePath?: (number | string)[] },
    options: { body?: T; locale?: Locale; queryParams?: QueryParams; timeoutMs?: number } = {},
  ) {
    const { blob, filename } = await this.fetchFileBlob(location, options);

    this.downloadFromBlob({ blob, filename: filename || 'document.pdf' });
  }

  public async fetchFileUrl(
    {
      endpoint,
      routePath = [],
    }: {
      endpoint: BackendRoute;
      routePath?: (number | string)[];
    },
    queryParams?: QueryParams,
  ): Promise<string> {
    const response = await this.requestWithAuthResponse(endpoint + this.buildRoutePath(routePath), {
      method: 'GET',
      queryParams,
    });

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  public uploadFile<TResponse = Response>() {
    const xhr = new XMLHttpRequest();

    const getCallback = async (
      { endpoint, routePath = [] }: { endpoint: BackendRoute; routePath?: (number | string)[] },
      [key, file]: [string, File],
      params?: {
        formFields?: Record<string, string>;
        headers?: Record<string, string>;
        locale?: Locale;
        onAboard?: () => void;
        onUploadProgress?: (progress: number) => void;
        queryParams?: QueryParams;
        requestMethod?: 'PATCH' | 'POST' | 'PUT';
      },
    ) => {
      await this.refreshTokens();
      const accessToken = accessTokenStorage.getState().get();

      const reqMethod = params?.requestMethod || 'POST';
      const onUploadProgress = params?.onUploadProgress;
      const onAboard = params?.onAboard;
      const queryParams = params?.queryParams;
      const formFields = params?.formFields;

      const fileData = new FormData();
      fileData.append(key, file);
      if (formFields) {
        for (const [fieldKey, value] of Object.entries(formFields)) {
          fileData.append(fieldKey, value);
        }
      }
      const promiseCallback = new Promise<TResponse>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onUploadProgress) {
            onUploadProgress((event.loaded / event.total) * 100);
          }
        });
        xhr.onabort = () => onAboard?.();

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            rememberUploadedFile(xhr.response, file);
            resolve(xhr.response);
          } else {
            reject(
              new HttpException(xhr.response, JSON.parse(xhr.response) as { message?: string }),
            );
          }
        };

        const queries = queryParams ? this.queryParamBuilder.buildQuery(queryParams) : '';

        xhr.open(
          reqMethod,
          this.getJoinedPath(endpoint + this.buildRoutePath(routePath) + queries),
          true,
        );

        xhr.withCredentials = true;
        xhr.setRequestHeader('locale', params?.locale || '');
        xhr.setRequestHeader('Authorization', 'Bearer ' + accessToken);
        if (params?.headers) {
          for (const [headerKey, headerValue] of Object.entries(params.headers)) {
            xhr.setRequestHeader(headerKey, headerValue);
          }
        }
        xhr.send(fileData);
      });

      return promiseCallback;
    };

    return {
      abort: () => xhr.abort(),
      callback: getCallback,
    };
  }

  // Direct-to-S3 PUT against a presigned URL — no auth header, no credentials; the
  // signature in the URL is the authorization. Content-Type must match the signed one.
  public uploadFileToPresignedUrl() {
    const xhr = new XMLHttpRequest();

    const getCallback = (
      uploadUrl: string,
      file: File,
      params?: {
        onAboard?: () => void;
        onUploadProgress?: (progress: number) => void;
      },
    ) =>
      new Promise<void>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && params?.onUploadProgress) {
            params.onUploadProgress((event.loaded / event.total) * 100);
          }
        });
        xhr.onabort = () => params?.onAboard?.();
        xhr.onerror = () => reject(new Error('File upload failed'));
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`File upload failed: HTTP ${xhr.status}`));
          }
        };

        xhr.open('PUT', uploadUrl, true);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

    return {
      abort: () => xhr.abort(),
      callback: getCallback,
    };
  }

  public async get<T>(
    {
      endpoint,
      routePath = [],
    }: {
      endpoint: BackendRoute;
      routePath?: (number | string)[];
    },
    params: { locale?: Locale; queryParams?: QueryParams } = {},
  ) {
    return this.requestWithAuth<T>(endpoint + this.buildRoutePath(routePath), {
      ...params,
    });
  }

  public getJoinedPath(endpoint: string) {
    return `${this.backendUrl}${endpoint}`;
  }

  public async patch<T, B>(
    { endpoint, routePath = [] }: { endpoint: BackendRoute; routePath?: (number | string)[] },
    {
      body,
      headers,
      ...params
    }: {
      body?: B;
      headers?: Record<string, string>;
      locale?: Locale;
      queryParams?: QueryParams;
    } = {},
  ) {
    return this.requestWithAuth<T>(endpoint + this.buildRoutePath(routePath), {
      init: {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers,
      },
      ...params,
    });
  }

  public async put<T, B>(
    { endpoint, routePath = [] }: { endpoint: BackendRoute; routePath?: (number | string)[] },
    {
      body,
      headers,
      ...params
    }: {
      body?: B;
      headers?: Record<string, string>;
      locale?: Locale;
      queryParams?: QueryParams;
    } = {},
  ) {
    return this.requestWithAuth<T>(endpoint + this.buildRoutePath(routePath), {
      init: {
        method: 'PUT',
        body: JSON.stringify(body),
        headers,
      },
      ...params,
    });
  }

  public async post<T, B = unknown>(
    { endpoint, routePath = [] }: { endpoint: BackendRoute; routePath?: (number | string)[] },
    {
      body,
      headers,
      ...params
    }: {
      body?: B;
      headers?: Record<string, string>;
      locale?: Locale;
      queryParams?: QueryParams;
    } = {},
  ) {
    return this.requestWithAuth<T>(endpoint + this.buildRoutePath(routePath), {
      init: {
        method: 'POST',
        body: JSON.stringify(body),
        headers,
      },
      ...params,
    });
  }

  public async refreshTokens() {
    if (this.refreshLocked) {
      return new Promise<void>((resolve, reject) => {
        this.refreshLockCallbacks.push((error?: Error) => {
          if (error) {
            reject(error);
          } else {
            resolve(undefined);
          }
        });
      });
    }
    this.refreshLocked = true;
    this.refreshLockWatchdogId = setTimeout(
      () => this.releaseStuckRefreshLock(),
      REFRESH_LOCK_WATCHDOG_MS,
    );

    let refreshError: Error | undefined;
    try {
      const { accessToken } = await super.requestWithAuth<{
        accessToken: string;
      }>(this.getJoinedPath(BackendRoute.AUTH_REFRESH), {
        init: { signal: AbortSignal.timeout(REFRESH_REQUEST_TIMEOUT_MS) },
      });

      accessTokenStorage.getState().set(accessToken);
    } catch (error) {
      refreshError = error instanceof Error ? error : new Error(String(error));
      await this.clearStaleAuthSession();
      throw error;
    } finally {
      this.refreshLocked = false;
      this.notifyRefreshLock(refreshError);
    }
  }

  public async requestWithAuth<T>(
    route: string,
    {
      init,
      locale = Locale.EN,
      ...params
    }: {
      init?: RequestInit | undefined;
      locale?: Locale;
      queryParams?: QueryParams;
    } = {},
  ): Promise<T> {
    const execute = async (): Promise<T> => {
      const accessToken = accessTokenStorage.getState().get() || '';

      const url = this.getJoinedPath(route);

      const isFormData = init?.body instanceof FormData;
      const headers: Record<string, string> = {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(init?.headers as Record<string, string>),
        locale,
      };

      let res: T;

      try {
        res = await super.requestWithAuth<T>(url, {
          init: {
            ...init,
            headers,
          },
          ...params,
          accessToken,
        });
        return res;
      } catch (e) {
        if (!(e instanceof HttpException) || e.response?.status !== 401) throw e;
        if (this.isAuthRefreshRoute(url)) {
          await this.clearStaleAuthSession();
          throw e;
        }

        try {
          await this.refreshTokens();
        } catch (_ingore) {
          await this.clearStaleAuthSession();
          throw e;
        }
        res = await super.requestWithAuth<T>(url, {
          init: {
            ...init,
            headers,
          },
          ...params,
          accessToken: accessTokenStorage.getState().get() || '',
        });
        return res;
      }
    };

    // if (EVENTS_REQUEST_MIN_DELAY_MS > 0 && this.isEventsRoute(route)) {
    //   return withMinDuration(execute(), EVENTS_REQUEST_MIN_DELAY_MS);
    // }

    return execute();
  }

  public async postFormData<T>(
    {
      endpoint,
      routePath = [],
    }: {
      endpoint: BackendRoute;
      routePath?: (number | string)[];
    },
    formData: FormData,
  ): Promise<T> {
    return this.requestWithAuth<T>(endpoint + this.buildRoutePath(routePath), {
      init: {
        method: 'POST',
        body: formData,
      },
    });
  }

  private async parseErrorBody(response: Response) {
    return response
      .json()
      .catch(() => response.text())
      .catch(() => ({ message: 'Request failed' }));
  }

  public async requestWithAuthResponse(
    route: string,
    {
      body,
      headers,
      locale = Locale.EN,
      method = 'GET',
      queryParams,
    }: {
      body?: BodyInit | null;
      headers?: HeadersInit;
      locale?: Locale;
      method?: 'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT';
      queryParams?: QueryParams;
    } = {},
  ) {
    const url = super.buildQueryParam(this.getJoinedPath(route), queryParams);
    const makeRequest = async () => {
      return fetch(url, {
        body,
        method,
        credentials: 'include',
        signal: buildRequestTimeoutSignal(),
        headers: {
          Authorization: `Bearer ${accessTokenStorage.getState().get() || ''}`,
          locale,
          ...headers,
        },
      });
    };

    const response = await makeRequest();
    if (response.status === 401) {
      if (this.isAuthRefreshRoute(route)) {
        await this.clearStaleAuthSession();
        throw new HttpException(response, await this.parseErrorBody(response));
      }

      try {
        await this.refreshTokens();
      } catch (_ignore) {
        await this.clearStaleAuthSession();
        throw new HttpException(response, await this.parseErrorBody(response));
      }

      const retriedResponse = await makeRequest();
      if (!retriedResponse.ok) {
        throw new HttpException(retriedResponse, await this.parseErrorBody(retriedResponse));
      }

      return retriedResponse;
    }

    if (!response.ok) {
      throw new HttpException(response, await this.parseErrorBody(response));
    }

    return response;
  }
}

const apiService = new ApiService();

export default apiService;
